import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * HCM → Leave Configurator.
 *
 *   Tabs: Leave Types · Policies · Holidays · Shifts · Roster
 *   All scoped at Global / Office / Group Tag with GroupTag > Office > Global
 *   priority enforced by the backend resolver.
 */
export default function LeaveHCM() {
  const [tab, setTab] = useState('types');
  const [types, setTypes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [groupTags, setGroupTags] = useState([]);
  const [employees, setEmployees] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [tRes, pRes, hRes, sRes, oRes, gRes, eRes] = await Promise.all([
        apiFetch('/api/leave/types'),
        apiFetch('/api/leave/policies'),
        apiFetch('/api/leave/holidays'),
        apiFetch('/api/leave/hours'),
        apiFetch('/api/offices'),
        apiFetch('/api/metadata/group-tags').catch(() => ({ ok: false })),
        apiFetch('/api/employees?limit=500'),
      ]);
      if (tRes.ok) setTypes((await tRes.json()).types || []);
      if (pRes.ok) setPolicies((await pRes.json()).policies || []);
      if (hRes.ok) setHolidays((await hRes.json()).holidays || []);
      if (sRes.ok) setShifts((await sRes.json()).shifts || []);
      if (oRes.ok) setOffices((await oRes.json()).offices || []);
      if (gRes.ok) {
        const d = await gRes.json();
        setGroupTags(d.tags || d.rows || []);
      } else {
        // Fall back to /api/group-tags if that's the canonical route
        try {
          const alt = await apiFetch('/api/group-tags');
          if (alt.ok) { const d = await alt.json(); setGroupTags(d.tags || d.rows || []); }
        } catch (_) {}
      }
      if (eRes.ok) setEmployees((await eRes.json()).employees || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div style={{ padding: 20, maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          ['types',     'Leave Types'],
          ['policies',  'Policies · Eligibility'],
          ['holidays',  'Holiday Calendar'],
          ['shifts',    'Shifts · Working Hours'],
          ['roster',    'Day & Shift Roster'],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '7px 16px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
            border: '1px solid ' + (tab === k ? 'rgba(0,224,255,0.45)' : 'rgba(255,255,255,0.08)'),
            background: tab === k ? 'linear-gradient(135deg, rgba(0,224,255,0.18), rgba(59,143,244,0.18))' : 'transparent',
            color: tab === k ? '#fff' : '#94A3B8',
          }}>{lbl}</button>
        ))}
      </div>

      {tab === 'types'    && <TypesTab    types={types}     onReload={loadAll} />}
      {tab === 'policies' && <PoliciesTab types={types} policies={policies} offices={offices} groupTags={groupTags} onReload={loadAll} />}
      {tab === 'holidays' && <HolidaysTab holidays={holidays} offices={offices} groupTags={groupTags} onReload={loadAll} />}
      {tab === 'shifts'   && <ShiftsTab   shifts={shifts}    offices={offices} groupTags={groupTags} onReload={loadAll} />}
      {tab === 'roster'   && <RosterTab   shifts={shifts}    employees={employees} />}
    </div>
  );
}

/* ══════════════ LEAVE TYPES ══════════════ */
function TypesTab({ types, onReload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', category: 'Paid', color: '#60A5FA', is_half_day_allowed: true, can_be_clubbed: true, is_paid: true, sort_order: 100 });

  function openNew() { setEditing('new'); setForm({ code: '', name: '', category: 'Paid', color: '#60A5FA', is_half_day_allowed: true, can_be_clubbed: true, is_paid: true, sort_order: 100 }); }
  function openEdit(t) { setEditing(t.id); setForm({ ...t }); }
  async function save() {
    try {
      const url = editing === 'new' ? '/api/leave/types' : `/api/leave/types/${editing}`;
      const r = await apiFetch(url, { method: editing === 'new' ? 'POST' : 'PUT', body: JSON.stringify(form) });
      if (r.ok) { toast.success('Saved'); setEditing(null); onReload(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Save failed'); }
    } catch (e) { toast.error(e.message); }
  }
  async function remove(id) {
    if (!await toast.confirm({ title: 'Deactivate leave type?', body: 'It will remain in the history but won\'t be selectable.', danger: true, confirmLabel: 'Deactivate' })) return;
    const r = await apiFetch(`/api/leave/types/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Deactivated'); onReload(); }
  }

  return (
    <div style={cardStyle}>
      <HeaderRow title="Leave Types" subtitle={`${types.length} active`} actions={<button onClick={openNew} style={btnPrimary}>+ New type</button>} />
      <table style={tblStyle}>
        <thead><tr>
          <Th>Code</Th><Th>Name</Th><Th>Category</Th><Th align="center">Half-day</Th><Th align="center">Clubbing</Th><Th align="center">Paid</Th><Th align="right">Order</Th><Th></Th>
        </tr></thead>
        <tbody>
          {types.map(t => (
            <tr key={t.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Td><span style={{ ...chipStyle, background: (t.color || '#94A3B8') + '22', color: t.color || '#94A3B8' }}>{t.code}</span></Td>
              <Td>{t.name}</Td>
              <Td>{t.category}</Td>
              <Td align="center">{t.is_half_day_allowed ? '✓' : '—'}</Td>
              <Td align="center">{t.can_be_clubbed ? 'Allowed' : <span style={{ color: '#FBC56B' }}>Blocked</span>}</Td>
              <Td align="center">{t.is_paid ? '✓' : '—'}</Td>
              <Td align="right">{t.sort_order}</Td>
              <Td align="right">
                <button onClick={() => openEdit(t)} style={linkBtn}>Edit</button>
                <button onClick={() => remove(t.id)} style={{ ...linkBtn, color: '#FCA5A5', marginLeft: 10 }}>Deactivate</button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing === 'new' ? 'New Leave Type' : 'Edit Leave Type'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <Field label="Code"><input value={form.code || ''} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} style={inputStyle} /></Field>
            <Field label="Name"><input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Category">
              <select value={form.category || 'Paid'} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                <option>Paid</option><option>Unpaid</option><option>Statutory</option><option>WFH</option><option>Holiday</option>
              </select>
            </Field>
            <Field label="Colour"><input type="color" value={form.color || '#60A5FA'} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ ...inputStyle, padding: 3, height: 34 }} /></Field>
            <Field label="Gender restriction">
              {/* All = anyone can apply. Pick a specific value to restrict the
                  type to that gender (e.g. Maternity → Female, Paternity →
                  Male). The ESS Apply Leave dropdown filters by this. */}
              <select value={form.gender_restriction || 'All'}
                      onChange={e => setForm(f => ({ ...f, gender_restriction: e.target.value }))}
                      style={inputStyle}>
                <option value="All">All</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Others">Others</option>
              </select>
            </Field>
            <Field label="Sort order"><input type="number" value={form.sort_order ?? 100} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Flags" wide>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <Check checked={form.is_half_day_allowed !== false} onChange={v => setForm(f => ({ ...f, is_half_day_allowed: v }))}>Half-day allowed</Check>
                <Check checked={form.can_be_clubbed !== false} onChange={v => setForm(f => ({ ...f, can_be_clubbed: v }))}>Can be clubbed</Check>
                <Check checked={form.is_paid !== false} onChange={v => setForm(f => ({ ...f, is_paid: v }))}>Paid</Check>
              </div>
            </Field>
          </div>
          <ModalActions onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}
    </div>
  );
}

/* ══════════════ POLICIES ══════════════ */
function PoliciesTab({ types, policies, offices, groupTags, onReload }) {
  const [editing, setEditing] = useState(null);
  const emptyForm = { leave_type_id: '', scope: 'global', office_id: null, group_tag_id: null, quota_per_year: 0, accrual_method: 'pro_rata', pro_rata_from_doj: true, eligible_after_days: 0, blocked_for_trainee: false, blocked_for_probation: false, gender_restriction: null, min_notice_days: 0, max_consecutive_days: 0, carry_forward_max_days: null, expiry_days: null, doc_required_if_days_gt: null, requires_approval: true, auto_approve_if_days_leq: null, requires_secondary_approval_if_days_gt: null, is_active: true };
  const [form, setForm] = useState(emptyForm);

  function openNew() { setEditing('new'); setForm(emptyForm); }
  function openEdit(p) { setEditing(p.id); setForm({ ...emptyForm, ...p }); }
  async function save() {
    try {
      if (!form.leave_type_id) { toast.error('Leave type required'); return; }
      const url = editing === 'new' ? '/api/leave/policies' : `/api/leave/policies/${editing}`;
      const r = await apiFetch(url, { method: editing === 'new' ? 'POST' : 'PUT', body: JSON.stringify(form) });
      if (r.ok) { toast.success('Saved'); setEditing(null); onReload(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Save failed'); }
    } catch (e) { toast.error(e.message); }
  }
  async function remove(id) {
    if (!await toast.confirm({ title: 'Delete policy?', body: 'This cannot be undone.', danger: true, confirmLabel: 'Delete' })) return;
    const r = await apiFetch(`/api/leave/policies/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Deleted'); onReload(); }
  }

  return (
    <div style={cardStyle}>
      <HeaderRow
        title="Policies · Eligibility & Quotas"
        subtitle="Configure at Global, Office or Group-Tag level. Priority: Group Tag > Office > Global"
        actions={<button onClick={openNew} style={btnPrimary}>+ New policy</button>}
      />
      <table style={tblStyle}>
        <thead><tr>
          <Th>Leave</Th><Th>Scope</Th><Th align="right">Qty/yr</Th><Th>Accrual</Th>
          <Th align="center">Probation lock</Th><Th align="center">Trainee lock</Th>
          <Th align="right">Min notice</Th><Th align="right">Max consec</Th>
          <Th align="right">Doc if &gt;</Th><Th align="right">Auto-appr ≤</Th><Th></Th>
        </tr></thead>
        <tbody>
          {policies.map(p => (
            <tr key={p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Td>{p.leave_type_code}</Td>
              <Td><ScopeChip scope={p.scope} office={p.office_name} tag={p.group_tag_name} /></Td>
              <Td align="right">{p.quota_per_year}</Td>
              <Td>{p.accrual_method}</Td>
              <Td align="center">{p.blocked_for_probation ? '✓' : '—'}</Td>
              <Td align="center">{p.blocked_for_trainee ? '✓' : '—'}</Td>
              <Td align="right">{p.min_notice_days || '—'}</Td>
              <Td align="right">{p.max_consecutive_days || '—'}</Td>
              <Td align="right">{p.doc_required_if_days_gt ?? '—'}</Td>
              <Td align="right">{p.auto_approve_if_days_leq ?? '—'}</Td>
              <Td align="right">
                <button onClick={() => openEdit(p)} style={linkBtn}>Edit</button>
                <button onClick={() => remove(p.id)} style={{ ...linkBtn, color: '#FCA5A5', marginLeft: 10 }}>Delete</button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing === 'new' ? 'New Policy' : 'Edit Policy'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <Field label="Leave type">
              <select value={form.leave_type_id || ''} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inputStyle}>
                <option value="">Select…</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
              </select>
            </Field>
            <Field label="Scope (priority: Group Tag > Office > Global)">
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value, office_id: null, group_tag_id: null }))} style={inputStyle}>
                <option value="global">Global</option>
                <option value="office">Office</option>
                <option value="group_tag">Group Tag</option>
              </select>
            </Field>
            {form.scope === 'office' && (
              <Field label="Office" wide>
                <select value={form.office_id || ''} onChange={e => setForm(f => ({ ...f, office_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select office…</option>
                  {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            )}
            {form.scope === 'group_tag' && (
              <Field label="Group Tag" wide>
                <select value={form.group_tag_id || ''} onChange={e => setForm(f => ({ ...f, group_tag_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select group tag…</option>
                  {groupTags.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Quota / year"><input type="number" value={form.quota_per_year ?? 0} onChange={e => setForm(f => ({ ...f, quota_per_year: parseFloat(e.target.value) || 0 }))} style={inputStyle} /></Field>
            <Field label="Accrual method">
              <select value={form.accrual_method} onChange={e => setForm(f => ({ ...f, accrual_method: e.target.value }))} style={inputStyle}>
                <option value="yearly">Yearly</option>
                <option value="monthly">Monthly</option>
                <option value="pro_rata">Pro-rata (from DOJ)</option>
                <option value="one_shot">One shot</option>
                <option value="earned">Earned (on request)</option>
              </select>
            </Field>
            <Field label="Min notice (days)"><input type="number" value={form.min_notice_days ?? 0} onChange={e => setForm(f => ({ ...f, min_notice_days: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Max consecutive"><input type="number" value={form.max_consecutive_days ?? 0} onChange={e => setForm(f => ({ ...f, max_consecutive_days: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Doc required if days >"><input type="number" value={form.doc_required_if_days_gt ?? ''} onChange={e => setForm(f => ({ ...f, doc_required_if_days_gt: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} style={inputStyle} placeholder="Leave empty for no req" /></Field>
            <Field label="Auto-approve if days ≤"><input type="number" step="0.5" value={form.auto_approve_if_days_leq ?? ''} onChange={e => setForm(f => ({ ...f, auto_approve_if_days_leq: e.target.value === '' ? null : parseFloat(e.target.value) }))} style={inputStyle} /></Field>
            <Field label="Gender restriction">
              <select value={form.gender_restriction || ''} onChange={e => setForm(f => ({ ...f, gender_restriction: e.target.value || null }))} style={inputStyle}>
                <option value="">None</option><option value="F">Female only</option><option value="M">Male only</option>
              </select>
            </Field>
            <Field label="Eligible after (days of service)"><input type="number" value={form.eligible_after_days ?? 0} onChange={e => setForm(f => ({ ...f, eligible_after_days: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Carry-forward max (days)"><input type="number" value={form.carry_forward_max_days ?? ''} onChange={e => setForm(f => ({ ...f, carry_forward_max_days: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} style={inputStyle} placeholder="Empty = no carry" /></Field>
            <Field label="Expiry (days)"><input type="number" value={form.expiry_days ?? ''} onChange={e => setForm(f => ({ ...f, expiry_days: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} style={inputStyle} placeholder="e.g. 90 for comp-off" /></Field>
            <Field label="Secondary approval if days >"><input type="number" value={form.requires_secondary_approval_if_days_gt ?? ''} onChange={e => setForm(f => ({ ...f, requires_secondary_approval_if_days_gt: e.target.value === '' ? null : parseInt(e.target.value, 10) }))} style={inputStyle} /></Field>
            <Field label="Flags" wide>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <Check checked={!!form.pro_rata_from_doj} onChange={v => setForm(f => ({ ...f, pro_rata_from_doj: v }))}>Pro-rata from DOJ</Check>
                <Check checked={!!form.blocked_for_probation} onChange={v => setForm(f => ({ ...f, blocked_for_probation: v }))}>Blocked for Probation</Check>
                <Check checked={!!form.blocked_for_trainee} onChange={v => setForm(f => ({ ...f, blocked_for_trainee: v }))}>Blocked for Trainee</Check>
                <Check checked={form.requires_approval !== false} onChange={v => setForm(f => ({ ...f, requires_approval: v }))}>Requires approval</Check>
                <Check checked={form.is_active !== false} onChange={v => setForm(f => ({ ...f, is_active: v }))}>Active</Check>
              </div>
            </Field>
          </div>
          <ModalActions onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}
    </div>
  );
}

/* ══════════════ HOLIDAYS ══════════════ */
function HolidaysTab({ holidays, offices, groupTags, onReload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ holiday_date: '', name: '', category: 'general', scope: 'global', office_id: null, group_tag_id: null });

  async function add() {
    try {
      const r = await apiFetch('/api/leave/holidays', { method: 'POST', body: JSON.stringify(form) });
      if (r.ok) { toast.success('Added'); setAdding(false); onReload(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Failed'); }
    } catch (e) { toast.error(e.message); }
  }
  async function remove(id) {
    if (!await toast.confirm({ title: 'Delete holiday?', danger: true })) return;
    const r = await apiFetch(`/api/leave/holidays/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Deleted'); onReload(); }
  }

  return (
    <div style={cardStyle}>
      <HeaderRow title="Holiday Calendar" subtitle={`${holidays.length} holidays`} actions={<button onClick={() => { setAdding(true); setForm({ holiday_date: '', name: '', category: 'general', scope: 'global', office_id: null, group_tag_id: null }); }} style={btnPrimary}>+ Add holiday</button>} />
      <table style={tblStyle}>
        <thead><tr>
          <Th>Date</Th><Th>Name</Th><Th>Category</Th><Th>Scope</Th><Th></Th>
        </tr></thead>
        <tbody>
          {holidays.map(h => (
            <tr key={h.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Td>{new Date(h.holiday_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Td>
              <Td>{h.name}</Td>
              <Td><span style={{ ...chipStyle, background: holidayBg(h.category), color: holidayBorder(h.category) }}>{h.category}</span></Td>
              <Td><ScopeChip scope={h.scope} office={h.office_name} tag={h.group_tag_name} /></Td>
              <Td align="right"><button onClick={() => remove(h.id)} style={{ ...linkBtn, color: '#FCA5A5' }}>Delete</button></Td>
            </tr>
          ))}
        </tbody>
      </table>

      {adding && (
        <Modal onClose={() => setAdding(false)} title="Add Holiday">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <Field label="Date"><input type="date" value={form.holiday_date} onChange={e => setForm(f => ({ ...f, holiday_date: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Category">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                <option value="national">National</option><option value="general">General</option><option value="restricted">Restricted</option><option value="regional">Regional</option>
              </select>
            </Field>
            <Field label="Scope">
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value, office_id: null, group_tag_id: null }))} style={inputStyle}>
                <option value="global">Global</option><option value="office">Office</option><option value="group_tag">Group Tag</option>
              </select>
            </Field>
            {form.scope === 'office' && (
              <Field label="Office" wide>
                <select value={form.office_id || ''} onChange={e => setForm(f => ({ ...f, office_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select…</option>{offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            )}
            {form.scope === 'group_tag' && (
              <Field label="Group Tag" wide>
                <select value={form.group_tag_id || ''} onChange={e => setForm(f => ({ ...f, group_tag_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select…</option>{groupTags.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </Field>
            )}
          </div>
          <ModalActions onCancel={() => setAdding(false)} onSave={add} />
        </Modal>
      )}
    </div>
  );
}

/* ══════════════ SHIFTS ══════════════ */
function ShiftsTab({ shifts, offices, groupTags, onReload }) {
  const [editing, setEditing] = useState(null);
  const empty = { name: '', scope: 'global', office_id: null, group_tag_id: null, shift_start: '09:00', shift_end: '18:00', lunch_minutes: 60, lunch_window_start: '12:30', lunch_window_end: '14:00', working_days_mask: 62, max_hours_per_week: 40, wfh_allowed: true, wfh_requires_permission: true, is_active: true };
  const [form, setForm] = useState(empty);

  function openNew() { setEditing('new'); setForm(empty); }
  function openEdit(s) { setEditing(s.id); setForm({ ...empty, ...s }); }
  async function save() {
    try {
      const url = editing === 'new' ? '/api/leave/hours' : `/api/leave/hours/${editing}`;
      const r = await apiFetch(url, { method: editing === 'new' ? 'POST' : 'PUT', body: JSON.stringify(form) });
      if (r.ok) { toast.success('Saved'); setEditing(null); onReload(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.error || 'Save failed'); }
    } catch (e) { toast.error(e.message); }
  }

  const DAYS = [['Mon', 1], ['Tue', 2], ['Wed', 4], ['Thu', 8], ['Fri', 16], ['Sat', 32], ['Sun', 64]];
  function hasDay(mask, bit) { return (mask & bit) !== 0; }
  function toggleDay(bit) { setForm(f => ({ ...f, working_days_mask: hasDay(f.working_days_mask, bit) ? f.working_days_mask & ~bit : f.working_days_mask | bit })); }

  return (
    <div style={cardStyle}>
      <HeaderRow title="Shifts · Working Hours" subtitle="Per Global / Office / Group Tag. Priority: Group Tag > Office > Global" actions={<button onClick={openNew} style={btnPrimary}>+ New shift</button>} />
      <table style={tblStyle}>
        <thead><tr>
          <Th>Name</Th><Th>Scope</Th><Th>Hours</Th><Th>Lunch</Th><Th align="right">Max/week</Th><Th>Days</Th><Th></Th>
        </tr></thead>
        <tbody>
          {shifts.map(s => (
            <tr key={s.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Td>{s.name}</Td>
              <Td><ScopeChip scope={s.scope} office={s.office_name} tag={s.group_tag_name} /></Td>
              <Td>{s.shift_start} – {s.shift_end}</Td>
              <Td>{s.lunch_minutes}m ({s.lunch_window_start}–{s.lunch_window_end})</Td>
              <Td align="right">{s.max_hours_per_week}h</Td>
              <Td>{DAYS.filter(([, b]) => hasDay(s.working_days_mask, b)).map(([n]) => n).join(' ')}</Td>
              <Td align="right"><button onClick={() => openEdit(s)} style={linkBtn}>Edit</button></Td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing === 'new' ? 'New Shift' : 'Edit Shift'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <Field label="Name"><input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Scope">
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value, office_id: null, group_tag_id: null }))} style={inputStyle}>
                <option value="global">Global</option><option value="office">Office</option><option value="group_tag">Group Tag</option>
              </select>
            </Field>
            {form.scope === 'office' && (
              <Field label="Office" wide>
                <select value={form.office_id || ''} onChange={e => setForm(f => ({ ...f, office_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select…</option>{offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            )}
            {form.scope === 'group_tag' && (
              <Field label="Group Tag" wide>
                <select value={form.group_tag_id || ''} onChange={e => setForm(f => ({ ...f, group_tag_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">Select…</option>{groupTags.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Shift start"><input type="time" value={form.shift_start} onChange={e => setForm(f => ({ ...f, shift_start: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Shift end"><input type="time" value={form.shift_end} onChange={e => setForm(f => ({ ...f, shift_end: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Lunch minutes"><input type="number" value={form.lunch_minutes} onChange={e => setForm(f => ({ ...f, lunch_minutes: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Lunch window start"><input type="time" value={form.lunch_window_start} onChange={e => setForm(f => ({ ...f, lunch_window_start: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Lunch window end"><input type="time" value={form.lunch_window_end} onChange={e => setForm(f => ({ ...f, lunch_window_end: e.target.value }))} style={inputStyle} /></Field>
            <Field label="Max hours / week"><input type="number" value={form.max_hours_per_week} onChange={e => setForm(f => ({ ...f, max_hours_per_week: parseInt(e.target.value, 10) || 0 }))} style={inputStyle} /></Field>
            <Field label="Working days" wide>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map(([n, b]) => (
                  <button key={n} type="button" onClick={() => toggleDay(b)} style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: '1px solid ' + (hasDay(form.working_days_mask, b) ? 'rgba(0,224,255,0.45)' : 'rgba(255,255,255,0.10)'),
                    background: hasDay(form.working_days_mask, b) ? 'rgba(0,224,255,0.12)' : 'rgba(255,255,255,0.04)',
                    color: hasDay(form.working_days_mask, b) ? '#6FE6FF' : '#94A3B8',
                  }}>{n}</button>
                ))}
              </div>
            </Field>
            <Field label="WFH" wide>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
                <Check checked={!!form.wfh_allowed} onChange={v => setForm(f => ({ ...f, wfh_allowed: v }))}>WFH allowed</Check>
                <Check checked={!!form.wfh_requires_permission} onChange={v => setForm(f => ({ ...f, wfh_requires_permission: v }))}>Requires prior written permission</Check>
                <Check checked={form.is_active !== false} onChange={v => setForm(f => ({ ...f, is_active: v }))}>Active</Check>
              </div>
            </Field>
          </div>
          <ModalActions onCancel={() => setEditing(null)} onSave={save} />
        </Modal>
      )}
    </div>
  );
}

/* ══════════════ ROSTER ══════════════ */
function RosterTab({ shifts, employees }) {
  // Only active employees show up in the roster grid and bulk-assign.
  // Anyone Resigned / Inactive / Exited / Terminated is filtered out.
  const activeEmployees = useMemo(
    () => (employees || []).filter(e => {
      const s = String(e.status || 'Active').toUpperCase();
      return s === 'ACTIVE';
    }),
    [employees]
  );

  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
    return d.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [pickedShift, setPickedShift] = useState('');
  const [pickedDates, setPickedDates] = useState([]);

  const days = useMemo(() => {
    const arr = [];
    const d0 = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const d = new Date(d0); d.setDate(d0.getDate() + i);
      arr.push(d.toISOString().slice(0, 10));
    }
    return arr;
  }, [weekStart]);

  const load = useCallback(async () => {
    try {
      const to = new Date(weekStart); to.setDate(to.getDate() + 6);
      const r = await apiFetch(`/api/leave/roster?from=${weekStart}&to=${to.toISOString().slice(0, 10)}`);
      if (r.ok) setRows((await r.json()).roster || []);
    } catch (_) { /* ignore */ }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  function rosterCell(empId, date) {
    return rows.find(r => r.employee_id === empId && String(r.roster_date).slice(0, 10) === date);
  }

  async function applyRoster() {
    if (!pickedShift || selectedEmpIds.length === 0 || pickedDates.length === 0) {
      toast.error('Pick employees, shift, and at least one date'); return;
    }
    const payload = [];
    for (const eid of selectedEmpIds) for (const dt of pickedDates) {
      payload.push({ employee_id: eid, shift_id: pickedShift, roster_date: dt, day_type: 'work' });
    }
    const r = await apiFetch('/api/leave/roster/bulk', { method: 'POST', body: JSON.stringify(payload) });
    if (r.ok) { toast.success(`Updated ${payload.length} day(s)`); load(); setSelectedEmpIds([]); setPickedDates([]); }
  }

  return (
    <div style={cardStyle}>
      <HeaderRow title="Day & Shift Roster" subtitle="Assign shifts per employee per day. Overrides the default scoped shift." />

      {/* Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        <Field label="Week of">
          <input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Shift to assign">
          <select value={pickedShift} onChange={e => setPickedShift(e.target.value)} style={inputStyle}>
            <option value="">Select shift…</option>
            {shifts.map(s => <option key={s.id} value={s.id}>{s.name} · {s.shift_start}–{s.shift_end}</option>)}
          </select>
        </Field>
        <Field label="Pick days" wide>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {days.map(d => {
              const on = pickedDates.includes(d);
              return (
                <button key={d} type="button" onClick={() => setPickedDates(p => on ? p.filter(x => x !== d) : [...p, d])} style={{
                  padding: '6px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                  border: '1px solid ' + (on ? 'rgba(0,224,255,0.45)' : 'rgba(255,255,255,0.10)'),
                  background: on ? 'rgba(0,224,255,0.12)' : 'rgba(255,255,255,0.04)',
                  color: on ? '#6FE6FF' : '#94A3B8',
                }}>
                  {new Date(d).toLocaleDateString('en-IN', { weekday: 'short' })}<br />{d.slice(5)}
                </button>
              );
            })}
          </div>
        </Field>
        <button onClick={applyRoster} style={btnPrimary}>Apply to selected</button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={tblStyle}>
          <thead><tr>
            <Th></Th>
            <Th>Employee</Th>
            {days.map(d => <Th key={d} align="center">{new Date(d).toLocaleDateString('en-IN', { weekday: 'short' })}<br /><span style={{ fontSize: 10, color: '#64748B' }}>{d.slice(5)}</span></Th>)}
          </tr></thead>
          <tbody>
            {activeEmployees.slice(0, 30).map(e => (
              <tr key={e.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <Td><input type="checkbox" checked={selectedEmpIds.includes(e.id)} onChange={ev => setSelectedEmpIds(s => ev.target.checked ? [...s, e.id] : s.filter(x => x !== e.id))} /></Td>
                <Td>{e.first_name} {e.last_name} <span style={{ color: '#64748B', fontSize: 11 }}>· {e.emp_code}</span></Td>
                {days.map(d => {
                  const cell = rosterCell(e.id, d);
                  return (
                    <Td key={d} align="center">
                      {cell ? (
                        <span style={{ ...chipStyle, background: 'rgba(0,224,255,0.14)', color: '#6FE6FF' }}>
                          {cell.shift_name || '—'}
                        </span>
                      ) : <span style={{ color: '#475569', fontSize: 11 }}>default</span>}
                    </Td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#64748B', marginTop: 10 }}>
        Showing {Math.min(30, activeEmployees.length)} of {activeEmployees.length} active employees
        {employees.length !== activeEmployees.length && ` (${employees.length - activeEmployees.length} inactive hidden)`}.
      </div>
    </div>
  );
}

/* ══════════════ Shared UI primitives ══════════════ */
function HeaderRow({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 4 }}>{subtitle}</div>}
      </div>
      {actions}
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
function Check({ checked, onChange, children }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}
function Modal({ title, children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0B1220', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
function ModalActions({ onCancel, onSave }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
      <button type="button" onClick={onCancel} style={btnGhost}>Cancel</button>
      <button type="button" onClick={onSave} style={btnPrimary}>Save</button>
    </div>
  );
}
function Th({ children, align }) { return <th style={{ textAlign: align || 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: '0.4px', padding: '8px 10px' }}>{children}</th>; }
function Td({ children, align }) { return <td style={{ fontSize: 13, color: '#E5E7EB', padding: '10px', textAlign: align || 'left' }}>{children}</td>; }
function ScopeChip({ scope, office, tag }) {
  const col = scope === 'group_tag' ? '#FF00E5' : scope === 'office' ? '#00E0FF' : '#94A3B8';
  const lbl = scope === 'group_tag' ? `Tag: ${tag || '—'}` : scope === 'office' ? `Office: ${office || '—'}` : 'Global';
  return <span style={{ ...chipStyle, background: col + '22', color: col, border: `1px solid ${col}55` }}>{lbl}</span>;
}
function holidayBg(cat) { return { national: 'rgba(239,68,68,0.14)', general: 'rgba(245,158,11,0.14)', restricted: 'rgba(148,163,184,0.18)', regional: 'rgba(59,143,244,0.14)' }[cat] || 'rgba(148,163,184,0.18)'; }
function holidayBorder(cat) { return { national: '#FCA5A5', general: '#FBC56B', restricted: '#CBD5E1', regional: '#8DC3FF' }[cat] || '#CBD5E1'; }

const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 18 };
const chipStyle = { fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, letterSpacing: '0.3px', textTransform: 'uppercase' };
const inputStyle = { padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%' };
const btnPrimary = { padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)', color: '#fff' };
const btnGhost   = { padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const linkBtn    = { all: 'unset', cursor: 'pointer', color: '#60A5FA', fontSize: 12 };
const tblStyle   = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 };
